import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getSite } from "@/lib/site";
import { setMeta, setCanonical } from "@/lib/seo";
import { Building2, BedDouble, Bath, Ruler, ArrowLeft, Home as HomeIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const currency = (n) => n ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n) : "Contact for price";

export default function PublicListings() {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [site, setSite] = useState({});

  useEffect(() => {
    axios.get(`${API}/lm/public/listings`).then(({ data }) => setListings(data)).catch(() => {}).finally(() => setLoading(false));
    getSite().then((s) => {
      setSite(s);
      if (s.browse_title) document.title = s.browse_title;
      setMeta("description", s.browse_description);
      setMeta("og:title", s.browse_title, true); setMeta("og:description", s.browse_description, true);
      if (s.canonical_host) { setCanonical(`https://${s.canonical_host}/browse`); setMeta("og:url", `https://${s.canonical_host}/browse`, true); }
    });
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFDFB]" data-testid="public-listings-page">
      <header className="bg-[#1F3C88] noise-bg">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-[#F7941D] flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <span className="font-serif text-xl text-white">MattC Team</span>
          </button>
          <button data-testid="public-back-home" onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" /> Home
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-xs tracking-[0.3em] uppercase text-[#F7941D] font-semibold">MattC Team</div>
        <h1 data-testid="browse-title" className="font-serif text-4xl sm:text-5xl tracking-tight text-[#0A0A0A] mt-2">{(site.browse_title || "Current Listings").split(" | ")[0]}</h1>
        <p data-testid="browse-intro" className="mt-3 text-[#575755]">{site.browse_intro || "Explore homes currently represented by our team."}</p>

        {loading ? (
          <div className="mt-16 text-center text-[#8a8a86]">Loading listings…</div>
        ) : listings.length === 0 ? (
          <div className="mt-16 flex flex-col items-center text-center text-[#8a8a86]">
            <HomeIcon className="w-10 h-10 text-[#C3D0EC] mb-3" strokeWidth={1.5} />
            No active listings right now — check back soon.
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.map((l) => (
              <button key={l.slug} data-testid={`public-listing-${l.slug}`} onClick={() => navigate(`/p/${l.slug}`)}
                className="group text-left bg-white border border-[#E5E5E2] rounded-sm overflow-hidden hover:border-[#F7941D] transition-colors duration-200">
                <div className="h-52 w-full overflow-hidden bg-[#EAF0FB]">
                  {l.hero_photo ? (
                    <img src={l.hero_photo} alt={l.address_line} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><HomeIcon className="w-10 h-10 text-[#C3D0EC]" strokeWidth={1.5} /></div>
                  )}
                </div>
                <div className="p-5">
                  <div className="font-serif text-2xl text-[#1F3C88]">{currency(l.price)}</div>
                  <div className="mt-1 text-[#0A0A0A] font-medium">{l.address_line}</div>
                  <div className="text-sm text-[#575755]">{[l.city, l.state, l.zip_code].filter(Boolean).join(", ")}</div>
                  {(l.beds || l.baths || l.sqft) && (
                    <div className="mt-3 pt-3 border-t border-[#F0EFEC] flex items-center gap-4 text-sm text-[#575755]">
                      {l.beds != null && <span className="flex items-center gap-1"><BedDouble className="w-4 h-4" />{l.beds}</span>}
                      {l.baths != null && <span className="flex items-center gap-1"><Bath className="w-4 h-4" />{l.baths}</span>}
                      {l.sqft != null && <span className="flex items-center gap-1"><Ruler className="w-4 h-4" />{Number(l.sqft).toLocaleString()}</span>}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {site.agents?.length > 0 && (
          <section className="mt-20" data-testid="meet-agents-section">
            <div className="text-xs tracking-[0.3em] uppercase text-[#F7941D] font-semibold">Our Team</div>
            <h2 className="font-serif text-3xl tracking-tight text-[#0A0A0A] mt-2">Meet our agents</h2>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {site.agents.map((a) => (
                <a key={a.username} href={`/a/${a.username}`} data-testid={`meet-agent-${a.username}`}
                  className="group flex items-center gap-3 bg-white border border-[#E5E5E2] rounded-sm p-4 hover:border-[#F7941D] transition-colors duration-200">
                  {a.photo_url ? <img src={a.photo_url} alt={a.name} loading="lazy" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} className="w-12 h-12 rounded-full object-cover bg-[#EAF0FB]" /> : <div className="w-12 h-12 rounded-full bg-[#EAF0FB]" />}
                  <div className="min-w-0"><div className="font-medium text-[#0A0A0A] truncate group-hover:text-[#1F3C88]">{a.name}</div><div className="text-xs text-[#8a8a86] truncate">{a.title}</div></div>
                </a>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
